import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { KuniriLogo } from '@/components/kuniri-logo'
import { AuthLanguageSwitcher } from '@/features/auth/components/locale-switcher'
import { currentLocale } from '@/lib/i18n'
import manifest from '@/assets/legal/manifest.json'
import privacyEn from '@/assets/legal/en/privacy-2026-10-25.1.txt?raw'
import termsEn from '@/assets/legal/en/terms-2026-10-21.1.txt?raw'
import privacyIt from '@/assets/legal/it/privacy-2026-10-25.1.txt?raw'
import termsIt from '@/assets/legal/it/terms-2026-10-21.1.txt?raw'

export type LegalKind = 'terms' | 'privacy'

const DOCUMENTS = {
  it: { terms: termsIt, privacy: privacyIt },
  en: { terms: termsEn, privacy: privacyEn },
} as const

interface LegalScreenProps {
  kind: LegalKind
}

interface LegalBlock {
  heading?: string
  body: string
}

/**
 * The legal files are plain text with blank-line paragraphs. The first two
 * blocks and every numbered section start with a title line, matching the
 * Flutter reader; the rest is body copy. Splitting here keeps the text
 * selectable and the version line intact.
 */
function parseBlocks(text: string): LegalBlock[] {
  return text
    .trim()
    .split(/\n\s*\n/)
    .map((block, index) => {
      const lines = block.split('\n')
      const first = lines[0]?.trim() ?? ''
      const heading =
        index === 0 ||
        index === 1 ||
        /^\d+\.\s/.test(first)
      if (!heading) return { body: block }
      return {
        heading: first,
        body: lines.slice(1).join('\n').trim(),
      }
    })
}

/**
 * Bundled legal text (D-038). Italian is the source; the English files are the
 * equally binding translation at the same version, pending the maintainer's
 * legal review outside the app.
 */
export function LegalScreen({ kind }: LegalScreenProps) {
  const { t, i18n } = useTranslation()
  const locale = currentLocale()
  const text = DOCUMENTS[locale][kind]
  const version = manifest[kind].version
  const blocks = parseBlocks(text)

  return (
    <main id="main-content" className="min-h-dvh bg-paper">
      <div className="mx-auto w-full max-w-[800px] px-6 pt-[calc(var(--safe-top)+12px)] pb-5 md:px-8 md:py-8">
        <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <Link to="/" className="inline-flex">
            <KuniriLogo height={34} />
          </Link>
          <div className="flex items-center gap-2">
            <AuthLanguageSwitcher />
            <Link to="/" className="btn-ghost min-h-11">
              {t('legal.backToApp')}
            </Link>
          </div>
        </header>

        <article>
          <h1 className="text-[25px] leading-none md:text-[32px]">
            {t(`screens.${kind}.title`)}
          </h1>
          <p className="mt-2 text-sm font-semibold text-muted">
            {t('legal.version', { version })}
          </p>

          <div
            className="mt-6 space-y-5 text-[16px] leading-[1.6] text-deep select-text"
            lang={locale}
            key={`${kind}-${i18n.language}`}
          >
            {blocks.map((block, index) => (
              <section key={`${kind}-${index}`}>
                {block.heading ? (
                  <h2 className="font-body text-[19px] leading-[1.35] font-bold">
                    {block.heading}
                  </h2>
                ) : null}
                {block.body ? (
                  <p className="mt-2 whitespace-pre-line break-words">
                    {block.body}
                  </p>
                ) : null}
              </section>
            ))}
          </div>
        </article>
      </div>
    </main>
  )
}
